import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminPageHeader, SyncButton, AdminEmptyState, ResponsiveTableWrap } from "@/components/admin/crud-helpers";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { format, subDays, startOfMonth, endOfMonth, startOfDay, endOfDay, subMonths } from "date-fns";
import { CalendarIcon, Calculator, Download, FileText, CheckCircle2, XCircle, Wallet, Trash2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/branch-payouts")({
  component: BranchPayoutsPage,
});

type Branch = {
  id: string; name: string; code: string; type: string; status: string;
  commission_rate: number; email: string | null;
};
type Order = {
  id: string; branch_id: string | null; status: string; total_cents: number;
  currency: string; created_at: string; paid_at: string | null;
};
type Payout = {
  id: string; branch_id: string; period_start: string; period_end: string;
  revenue_cents: number; commission_rate: number; amount_cents: number;
  currency: string; status: "pending" | "paid" | "void"; paid_at: string | null;
  notes: string | null; created_at: string;
};

type Preset = "this_month" | "last_month" | "30d" | "90d" | "custom";

function BranchPayoutsPage() {
  const qc = useQueryClient();
  const [preset, setPreset] = useState<Preset>("last_month");
  const [from, setFrom] = useState<Date | undefined>(startOfMonth(subMonths(new Date(), 1)));
  const [to, setTo] = useState<Date | undefined>(endOfMonth(subMonths(new Date(), 1)));
  const [onlyFranchise, setOnlyFranchise] = useState(true);
  const [generating, setGenerating] = useState<string | null>(null);
  const [editing, setEditing] = useState<Payout | null>(null);
  const [payoutFilter, setPayoutFilter] = useState<"all" | Payout["status"]>("all");
  const [payoutSearch, setPayoutSearch] = useState("");

  const range = useMemo(() => resolveRange(preset, from, to), [preset, from, to]);

  const branches = useQuery({
    queryKey: ["branches_payouts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("branches")
        .select("id, name, code, type, status, commission_rate, email")
        .order("name");
      if (error) throw error;
      return (data ?? []) as Branch[];
    },
  });

  const orders = useQuery({
    queryKey: ["orders_payouts", range.from.toISOString(), range.to.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select("id, branch_id, status, total_cents, currency, created_at, paid_at")
        .gte("created_at", range.from.toISOString())
        .lte("created_at", range.to.toISOString());
      if (error) throw error;
      return (data ?? []) as Order[];
    },
  });

  const payouts = useQuery({
    queryKey: ["branch_payouts_list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("branch_payouts")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Payout[];
    },
  });

  const calculations = useMemo(() => {
    const list = (branches.data ?? []).filter(b => !onlyFranchise || b.type === "franchise");
    const ordersByBranch = new Map<string, Order[]>();
    for (const o of orders.data ?? []) {
      if (!o.branch_id) continue;
      const arr = ordersByBranch.get(o.branch_id) ?? [];
      arr.push(o);
      ordersByBranch.set(o.branch_id, arr);
    }
    return list.map(b => {
      const all = ordersByBranch.get(b.id) ?? [];
      const paid = all.filter(o => o.status === "paid" || !!o.paid_at);
      const refunded = all.filter(o => o.status === "refunded");
      const revenue = paid.reduce((s, o) => s + (o.total_cents ?? 0), 0)
                    - refunded.reduce((s, o) => s + (o.total_cents ?? 0), 0);
      const rate = Number(b.commission_rate ?? 0);
      const commission = Math.max(0, Math.round(revenue * rate / 100));
      return {
        branch: b,
        revenue,
        commission,
        rate,
        paidOrders: paid.length,
        refundedOrders: refunded.length,
      };
    }).sort((a, b) => b.commission - a.commission);
  }, [branches.data, orders.data, onlyFranchise]);

  const totals = useMemo(() => ({
    revenue: calculations.reduce((s, r) => s + r.revenue, 0),
    commission: calculations.reduce((s, r) => s + r.commission, 0),
    paidOrders: calculations.reduce((s, r) => s + r.paidOrders, 0),
    branches: calculations.length,
  }), [calculations]);

  const payoutStats = useMemo(() => {
    const rows = payouts.data ?? [];
    return {
      pending: rows.filter(r => r.status === "pending").reduce((s, r) => s + r.amount_cents, 0),
      paid: rows.filter(r => r.status === "paid").reduce((s, r) => s + r.amount_cents, 0),
      count: rows.length,
    };
  }, [payouts.data]);

  const branchMap = useMemo(() => new Map((branches.data ?? []).map(b => [b.id, b])), [branches.data]);

  const generatePayout = async (branchId: string, revenue: number, commission: number, rate: number) => {
    setGenerating(branchId);
    const { error } = await supabase.from("branch_payouts").insert({
      branch_id: branchId,
      period_start: format(range.from, "yyyy-MM-dd"),
      period_end: format(range.to, "yyyy-MM-dd"),
      revenue_cents: revenue,
      commission_rate: rate,
      amount_cents: commission,
      status: "pending",
    });
    setGenerating(null);
    if (error) toast.error(error.message);
    else {
      toast.success("Payout recorded");
      qc.invalidateQueries({ queryKey: ["branch_payouts_list"] });
    }
  };

  const generateAll = async () => {
    const rows = calculations.filter(c => c.commission > 0);
    if (!rows.length) { toast.info("No commissions to record"); return; }
    if (!confirm(`Generate ${rows.length} pending payouts?`)) return;
    const periodStart = format(range.from, "yyyy-MM-dd");
    const periodEnd = format(range.to, "yyyy-MM-dd");
    const payload = rows.map(r => ({
      branch_id: r.branch.id,
      period_start: periodStart,
      period_end: periodEnd,
      revenue_cents: r.revenue,
      commission_rate: r.rate,
      amount_cents: r.commission,
      status: "pending" as const,
    }));
    const { error } = await supabase.from("branch_payouts").insert(payload);
    if (error) toast.error(error.message);
    else { toast.success(`Recorded ${rows.length} payouts`); qc.invalidateQueries({ queryKey: ["branch_payouts_list"] }); }
  };

  const setStatus = async (id: string, status: Payout["status"]) => {
    const patch: Partial<Payout> = { status };
    if (status === "paid") patch.paid_at = new Date().toISOString();
    if (status !== "paid") patch.paid_at = null;
    const { error } = await supabase.from("branch_payouts").update(patch).eq("id", id);
    if (error) toast.error(error.message);
    else qc.invalidateQueries({ queryKey: ["branch_payouts_list"] });
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this payout record?")) return;
    const { error } = await supabase.from("branch_payouts").delete().eq("id", id);
    if (error) toast.error(error.message);
    else { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["branch_payouts_list"] }); }
  };

  const saveNotes = async (id: string, notes: string) => {
    const { error } = await supabase.from("branch_payouts").update({ notes }).eq("id", id);
    if (error) toast.error(error.message);
    else { toast.success("Saved"); setEditing(null); qc.invalidateQueries({ queryKey: ["branch_payouts_list"] }); }
  };

  const exportCsv = () => {
    const lines: string[] = [];
    lines.push(`Commission report,${format(range.from, "yyyy-MM-dd")} to ${format(range.to, "yyyy-MM-dd")}`);
    lines.push("");
    lines.push("Branch,Code,Type,Rate %,Paid orders,Refunded orders,Revenue,Commission");
    for (const c of calculations) {
      lines.push([
        csvEscape(c.branch.name), c.branch.code, c.branch.type,
        c.rate, c.paidOrders, c.refundedOrders,
        (c.revenue / 100).toFixed(2), (c.commission / 100).toFixed(2),
      ].join(","));
    }
    lines.push("");
    lines.push(`Total revenue,${(totals.revenue / 100).toFixed(2)}`);
    lines.push(`Total commission,${(totals.commission / 100).toFixed(2)}`);
    download(lines.join("\n"), `commissions-${format(range.from, "yyyyMMdd")}-${format(range.to, "yyyyMMdd")}.csv`, "text/csv");
  };

  const exportPdf = async () => {
    const { default: jsPDF } = await import("jspdf");
    const { default: autoTable } = await import("jspdf-autotable");
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Franchise commission report", 14, 18);
    doc.setFontSize(10);
    doc.text(`Period: ${format(range.from, "yyyy-MM-dd")} → ${format(range.to, "yyyy-MM-dd")}`, 14, 25);
    autoTable(doc, {
      startY: 32,
      head: [["Branch", "Code", "Type", "Rate %", "Paid", "Refunded", "Revenue", "Commission"]],
      body: calculations.map(c => [
        c.branch.name, c.branch.code, c.branch.type, c.rate,
        c.paidOrders, c.refundedOrders,
        fmt(c.revenue), fmt(c.commission),
      ]),
      foot: [["", "", "", "", "", "Totals", fmt(totals.revenue), fmt(totals.commission)]],
    });
    doc.save(`commissions-${format(range.from, "yyyyMMdd")}-${format(range.to, "yyyyMMdd")}.pdf`);
  };

  const exportPayoutsCsv = () => {
    const lines: string[] = [];
    lines.push("Branch,Code,Period,Revenue,Rate %,Amount,Status,Paid at,Notes");
    for (const p of payouts.data ?? []) {
      const b = branchMap.get(p.branch_id);
      lines.push([
        csvEscape(b?.name ?? p.branch_id), b?.code ?? "",
        `${p.period_start} to ${p.period_end}`,
        (p.revenue_cents / 100).toFixed(2), p.commission_rate,
        (p.amount_cents / 100).toFixed(2), p.status,
        p.paid_at ?? "", csvEscape(p.notes ?? ""),
      ].join(","));
    }
    download(lines.join("\n"), `payouts-${format(new Date(), "yyyyMMdd")}.csv`, "text/csv");
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Franchise commissions & payouts"
        description="Calculate commissions from completed orders and track payout status per branch."
        actions={
          <>
            <SyncButton onSync={() => { orders.refetch(); payouts.refetch(); }} loading={orders.isFetching || payouts.isFetching} />
            <Button asChild variant="outline" size="sm"><Link to="/admin/branches">Branches</Link></Button>
          </>
        }
      />

      <div className="flex flex-wrap gap-2 items-end">
        <div className="grid gap-1">
          <span className="text-xs text-muted-foreground">Period</span>
          <Select value={preset} onValueChange={(v) => setPreset(v as Preset)}>
            <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="this_month">This month</SelectItem>
              <SelectItem value="last_month">Last month</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="custom">Custom…</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {preset === "custom" && (
          <>
            <DateField label="From" value={from} onChange={setFrom} />
            <DateField label="To" value={to} onChange={setTo} />
          </>
        )}
        <div className="grid gap-1">
          <span className="text-xs text-muted-foreground">Scope</span>
          <Select value={onlyFranchise ? "franchise" : "all"} onValueChange={(v) => setOnlyFranchise(v === "franchise")}>
            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="franchise">Franchises only</SelectItem>
              <SelectItem value="all">All branches</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs text-muted-foreground pb-2">{format(range.from, "PP")} → {format(range.to, "PP")}</div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={exportCsv}><Download className="mr-2 h-4 w-4" /> CSV</Button>
          <Button variant="outline" onClick={exportPdf}><FileText className="mr-2 h-4 w-4" /> PDF</Button>
          <Button onClick={generateAll}><Calculator className="mr-2 h-4 w-4" /> Generate payouts</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Eligible branches" value={String(totals.branches)} />
        <Stat label="Paid orders" value={String(totals.paidOrders)} />
        <Stat label="Revenue (period)" value={fmt(totals.revenue)} />
        <Stat label="Commission (period)" value={fmt(totals.commission)} tone="primary" />
        <Stat label="Payouts pending" value={fmt(payoutStats.pending)} tone="warn" />
        <Stat label="Payouts paid" value={fmt(payoutStats.paid)} tone="ok" />
        <Stat label="Payout records" value={String(payoutStats.count)} />
        <Stat label="Avg commission/branch" value={fmt(totals.branches ? Math.round(totals.commission / totals.branches) : 0)} />
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <div className="px-4 py-3 border-b border-border flex justify-between items-center">
            <div className="font-medium">Commission calculator</div>
            {orders.isLoading && <span className="text-xs text-muted-foreground">Loading orders…</span>}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-xs">
              <tr>
                <th className="px-4 py-2 text-left">Branch</th>
                <th className="px-4 py-2 text-left">Type</th>
                <th className="px-4 py-2 text-right">Rate</th>
                <th className="px-4 py-2 text-right">Paid</th>
                <th className="px-4 py-2 text-right">Refunded</th>
                <th className="px-4 py-2 text-right">Revenue</th>
                <th className="px-4 py-2 text-right">Commission</th>
                <th className="px-4 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {calculations.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-6 text-muted-foreground">No matching branches.</td></tr>
              )}
              {calculations.map(c => (
                <tr key={c.branch.id} className="border-t border-border hover:bg-secondary/40">
                  <td className="px-4 py-2">
                    <div className="font-medium">{c.branch.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{c.branch.code}</div>
                  </td>
                  <td className="px-4 py-2"><Badge variant="outline">{c.branch.type}</Badge></td>
                  <td className="px-4 py-2 text-right">{c.rate}%</td>
                  <td className="px-4 py-2 text-right">{c.paidOrders}</td>
                  <td className="px-4 py-2 text-right text-muted-foreground">{c.refundedOrders}</td>
                  <td className="px-4 py-2 text-right">{fmt(c.revenue)}</td>
                  <td className="px-4 py-2 text-right font-medium">{fmt(c.commission)}</td>
                  <td className="px-4 py-2 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={generating === c.branch.id || c.commission <= 0}
                      onClick={() => generatePayout(c.branch.id, c.revenue, c.commission, c.rate)}
                    >
                      <Wallet className="mr-2 h-4 w-4" /> Record
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            {calculations.length > 0 && (
              <tfoot>
                <tr className="border-t border-border bg-secondary/30 font-medium">
                  <td className="px-4 py-2" colSpan={5}>Totals</td>
                  <td className="px-4 py-2 text-right">{fmt(totals.revenue)}</td>
                  <td className="px-4 py-2 text-right">{fmt(totals.commission)}</td>
                  <td className="px-4 py-2" />
                </tr>
              </tfoot>
            )}
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
            <div className="font-medium">Payouts ledger</div>
            <div className="ml-auto flex flex-wrap gap-1.5">
              {(["all", "pending", "paid", "void"] as const).map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant={payoutFilter === s ? "default" : "outline"}
                  onClick={() => setPayoutFilter(s)}
                >
                  {s}
                </Button>
              ))}
              <input
                value={payoutSearch}
                onChange={(e) => setPayoutSearch(e.target.value)}
                placeholder="Search branch…"
                className="h-8 rounded-md border border-input bg-background px-2 text-xs"
              />
              <Button variant="outline" size="sm" onClick={exportPayoutsCsv}>
                <Download className="mr-1 h-4 w-4" /> CSV
              </Button>
            </div>
          </div>
          {(() => {
            const q = payoutSearch.trim().toLowerCase();
            const filtered = (payouts.data ?? []).filter((p) => {
              if (payoutFilter !== "all" && p.status !== payoutFilter) return false;
              if (!q) return true;
              const b = branchMap.get(p.branch_id);
              return [b?.name, b?.code].filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
            });
            if (!payouts.isLoading && filtered.length === 0) {
              return <div className="px-4 py-8"><AdminEmptyState title={q || payoutFilter !== "all" ? "No payouts match" : "No payouts recorded yet"} /></div>;
            }
            return (
              <ResponsiveTableWrap>
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50 text-xs">
                    <tr>
                      <th className="px-4 py-2 text-left">Branch</th>
                      <th className="hidden px-4 py-2 text-left md:table-cell">Period</th>
                      <th className="hidden px-4 py-2 text-right lg:table-cell">Revenue</th>
                      <th className="hidden px-4 py-2 text-right sm:table-cell">Rate</th>
                      <th className="px-4 py-2 text-right">Amount</th>
                      <th className="px-4 py-2 text-left">Status</th>
                      <th className="hidden px-4 py-2 text-left xl:table-cell">Paid at</th>
                      <th className="px-4 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.isLoading && <tr><td colSpan={8} className="px-4 py-6 text-muted-foreground">Loading…</td></tr>}
                    {filtered.map(p => {
                      const b = branchMap.get(p.branch_id);
                      return (
                        <tr key={p.id} className="border-t border-border hover:bg-secondary/40">
                          <td className="px-4 py-2">
                            <div className="font-medium">{b?.name ?? "—"}</div>
                            <div className="font-mono text-xs text-muted-foreground">
                              {b?.code ?? p.branch_id.slice(0, 8)}
                              <span className="md:hidden"> · {p.period_start}</span>
                            </div>
                          </td>
                          <td className="hidden px-4 py-2 text-muted-foreground md:table-cell">{p.period_start} → {p.period_end}</td>
                          <td className="hidden px-4 py-2 text-right lg:table-cell">{fmt(p.revenue_cents)}</td>
                          <td className="hidden px-4 py-2 text-right sm:table-cell">{p.commission_rate}%</td>
                          <td className="px-4 py-2 text-right font-medium">{fmt(p.amount_cents)}</td>
                          <td className="px-4 py-2">
                            {p.status === "paid" && <Badge className="bg-green-600 text-white hover:bg-green-600">paid</Badge>}
                            {p.status === "pending" && <Badge className="bg-amber-500 text-white hover:bg-amber-500">pending</Badge>}
                            {p.status === "void" && <Badge variant="outline">void</Badge>}
                          </td>
                          <td className="hidden px-4 py-2 text-muted-foreground xl:table-cell">{p.paid_at ? new Date(p.paid_at).toLocaleDateString() : "—"}</td>
                          <td className="px-4 py-2 text-right">
                            <div className="flex justify-end gap-1">
                              {p.status !== "paid" && (
                                <Button size="icon" variant="ghost" className="h-8 w-8" title="Mark paid" onClick={() => setStatus(p.id, "paid")}>
                                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                                </Button>
                              )}
                              {p.status !== "void" && (
                                <Button size="icon" variant="ghost" className="h-8 w-8" title="Void" onClick={() => setStatus(p.id, "void")}>
                                  <XCircle className="h-4 w-4 text-muted-foreground" />
                                </Button>
                              )}
                              <Button size="sm" variant="ghost" onClick={() => setEditing(p)}>Notes</Button>
                              <Button size="icon" variant="ghost" className="h-8 w-8" title="Delete" onClick={() => remove(p.id)}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </ResponsiveTableWrap>
            );
          })()}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Payout notes</DialogTitle></DialogHeader>
          {editing && (
            <NotesEditor payout={editing} onSave={(notes) => saveNotes(editing.id, notes)} />
          )}
          <DialogFooter />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NotesEditor({ payout, onSave }: { payout: Payout; onSave: (notes: string) => void }) {
  const [val, setVal] = useState(payout.notes ?? "");
  return (
    <div className="space-y-3">
      <Textarea rows={5} value={val} onChange={(e) => setVal(e.target.value)} placeholder="Payment reference, bank notes, etc." />
      <div className="flex justify-end">
        <Button onClick={() => onSave(val)}>Save</Button>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "primary" | "warn" | "ok" }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className={cn(
          "text-2xl font-semibold mt-1",
          tone === "primary" && "text-primary",
          tone === "warn" && "text-amber-500",
          tone === "ok" && "text-green-600",
        )}>{value}</div>
      </CardContent>
    </Card>
  );
}

function DateField({ label, value, onChange }: { label: string; value?: Date; onChange: (d?: Date) => void }) {
  return (
    <div className="grid gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className={cn("w-[180px] justify-start text-left font-normal", !value && "text-muted-foreground")}>
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value ? format(value, "PP") : "Pick a date"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={value} onSelect={onChange} initialFocus className={cn("p-3 pointer-events-auto")} />
        </PopoverContent>
      </Popover>
    </div>
  );
}

function resolveRange(preset: Preset, from?: Date, to?: Date) {
  const now = new Date();
  if (preset === "this_month") return { from: startOfMonth(now), to: endOfMonth(now) };
  if (preset === "last_month") {
    const lm = subMonths(now, 1);
    return { from: startOfMonth(lm), to: endOfMonth(lm) };
  }
  if (preset === "custom") {
    return { from: startOfDay(from ?? subDays(now, 30)), to: endOfDay(to ?? now) };
  }
  const days = preset === "30d" ? 30 : 90;
  return { from: startOfDay(subDays(now, days - 1)), to: endOfDay(now) };
}

function fmt(cents: number) {
  return (cents / 100).toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function csvEscape(s: string) {
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function download(content: string, filename: string, type: string) {
  const blob = new Blob([content], { type: `${type};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}
